import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { CALENDAR } from '@locales/id/messages';
import { calendarDayLong, calendarMonthTitle, WEEKDAYS } from '@lib/format';
import { addDays, addMonths, addMonthsToDay, clampDay, monthOf, monthWeeks, ordered, weekdayIndex, type Day } from '@lib/utils/calendar';
import { Icon } from './Icon';

interface Props {
  /** the chosen range ('' when none) */
  from: string;
  to: string;
  /** both ends picked (in order; the same day twice is one day) */
  onSelect: (from: Day, to: Day) => void;
  /** today (the caller's business day, e.g. WIB), marked in the grid */
  today: Day;
  /** the first / last day that can be picked */
  min?: Day;
  max?: Day;
}

/**
 * A month grid for picking a range of days (`from`/`to` set where it opens; mount it again to follow an outside
 * change): the first press sets the start, the second the end (a hover or the
 * keyboard previews the range in between). Arrows move by a day or a week, Home/End to the week's ends,
 * PageUp/PageDown by a month (with Shift a year), Enter or Space picks. Days are plain calendar dates; weeks start
 * on Monday; month and day names come from the locale.
 */
export function RangeCalendar({ from, to, onSelect, today, min, max }: Props) {
  const [anchor, setAnchor] = useState<Day | null>(null);
  const [hover, setHover] = useState<Day | null>(null);
  const [focus, setFocus] = useState<Day>(() => clampDay(to || from || today, min, max));
  const [month, setMonth] = useState(() => monthOf(focus));
  const grid = useRef<HTMLTableElement>(null);
  const moved = useRef(false);

  useEffect(() => {
    if (!moved.current) return;
    moved.current = false;
    grid.current?.querySelector<HTMLButtonElement>(`[data-day="${focus}"]`)?.focus();
  }, [focus, month]);

  const go = (d: Day) => {
    const c = clampDay(d, min, max);
    // clamped onto the day it is on: nothing re-renders, so nothing may wait to move the focus later
    moved.current = c !== focus || monthOf(c) !== month;
    setFocus(c);
    setMonth(monthOf(c));
    if (anchor) setHover(c);
  };
  const showMonth = (n: number) => {
    const m = addMonths(month, n);
    setMonth(m);
    setFocus(clampDay(monthOf(focus) === m ? focus : `${m}-01`, min, max));
  };
  const pick = (d: Day) => {
    if (!anchor) {
      setAnchor(d);
      setHover(d);
      return;
    }
    const [a, b] = ordered(anchor, d);
    setAnchor(null);
    setHover(null);
    onSelect(a, b);
  };
  const onKey = (e: KeyboardEvent<HTMLTableElement>) => {
    const step: Record<string, () => Day> = {
      ArrowLeft: () => addDays(focus, -1),
      ArrowRight: () => addDays(focus, 1),
      ArrowUp: () => addDays(focus, -7),
      ArrowDown: () => addDays(focus, 7),
      Home: () => addDays(focus, -weekdayIndex(focus)),
      End: () => addDays(focus, 6 - weekdayIndex(focus)),
      PageUp: () => addMonthsToDay(focus, e.shiftKey ? -12 : -1),
      PageDown: () => addMonthsToDay(focus, e.shiftKey ? 12 : 1),
    };
    const to = step[e.key];
    if (!to) return;
    // the popup around it must not treat the arrows as moving between its options
    e.preventDefault();
    e.stopPropagation();
    go(to());
  };

  const [lo, hi] = anchor ? ordered(anchor, hover ?? anchor) : [from, to];
  const out = (d: Day) => (!!min && d < min) || (!!max && d > max);
  const atMin = !!min && month <= monthOf(min);
  const atMax = !!max && month >= monthOf(max);
  const title = calendarMonthTitle(month);
  return (
    <div className="cal">
      <div className="cal-head">
        <button type="button" className="cal-nav" aria-label={CALENDAR.prevMonth} disabled={atMin} onClick={() => showMonth(-1)}>
          <Icon name="back" size={18} />
        </button>
        <span className="cal-title" aria-live="polite">{title}</span>
        <button type="button" className="cal-nav" aria-label={CALENDAR.nextMonth} disabled={atMax} onClick={() => showMonth(1)}>
          <Icon name="next" size={18} />
        </button>
      </div>
      <table className="cal-grid" role="grid" aria-label={title} ref={grid} onKeyDown={onKey}>
        <thead>
          <tr>
            {WEEKDAYS.map((w) => (
              <th key={w.short} scope="col" abbr={w.long}>
                {w.short}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {monthWeeks(month).map((week) => (
            <tr key={week[0]}>
              {week.map((d) => {
                if (monthOf(d) !== month) return <td key={d} role="gridcell" />;
                const inRange = !!lo && !!hi && d >= lo && d <= hi;
                const edge = d === lo || d === hi;
                const cls = ['cal-day', inRange ? 'in-range' : '', d === lo ? 'is-start' : '', d === hi ? 'is-end' : '', d === today ? 'is-today' : ''].filter(Boolean).join(' ');
                return (
                  <td key={d} role="gridcell" aria-selected={inRange} className={inRange && !edge ? 'cal-band' : inRange ? `cal-band ${d === lo && d !== hi ? 'from' : d === hi && d !== lo ? 'to' : 'one'}` : undefined}>
                    <button
                      type="button"
                      className={cls}
                      data-day={d}
                      tabIndex={d === focus ? 0 : -1}
                      disabled={out(d)}
                      aria-label={d === today ? `${calendarDayLong(d)}, ${CALENDAR.today}` : calendarDayLong(d)}
                      aria-current={d === today ? 'date' : undefined}
                      onClick={() => pick(d)}
                      onFocus={() => {
                        setFocus(d);
                        if (anchor) setHover(d);
                      }}
                      onMouseEnter={() => anchor && setHover(d)}
                    >
                      {Number(d.slice(8, 10))}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="cal-hint" aria-live="polite">
        {anchor ? CALENDAR.pickEnd : CALENDAR.pickStart}
      </p>
    </div>
  );
}
